import {Injectable} from '@angular/core';
import {environment} from "@env/environment";
import {getTimeZone} from "@core/utils/utils";
import { PersistencyService } from './persistency.service';
import { AppUserType, RequestOrigin } from '@core/models/domain/auth.domain';

@Injectable({
  providedIn: 'root'
})
/**
 * ApiService
 *
 * This service provides methods for constructing API URLs, retrieving service ports, and generating HTTP headers.
 * It is designed to facilitate communication with the backend by providing utility functions for API requests.
 *
 * Key Features:
 * - Retrieves service port numbers from the environment configuration.
 * - Constructs API URLs based on service names, domains, and SSL settings.
 * - Generates HTTP headers required for API requests, including authentication and user-specific information.
 */
export class ApiService {
  apiHostname: string;

  constructor( private persistency: PersistencyService) {
    this.apiHostname = environment.apiHostname;
  }

  /**
   * Retrieves the port number for a given service name from the environment configuration.
   *
   * This function looks up the port number for the specified service name in the environment configuration.
   * If the service name is not found, it returns undefined.
   *
   * @param {string} serviceName - The name of the service whose port number is to be retrieved.
   * @returns {number | undefined} The port number for the service, or undefined if not found.
   */
  getServicePort(serviceName: string): number | undefined {
    return (environment.services as Record<string, number> | undefined)?.[serviceName];
  }

  /**
   * Constructs the API URL for a specific service and domain.
   *
   * This function builds the complete API URL by combining the protocol, hostname, port, and domain.
   * The protocol is determined based on the environment's SSL setting.
   *
   * @param {string} serviceName - The name of the service for which the API URL is to be constructed.
   * @param {string} domain - The domain to be appended to the API URL.
   * @returns {string} The constructed API URL.
   */
  getApiURL(serviceName: string, domain: string): string {
    if (environment.useProxy) {
      return '/' + domain;
    }
    const https: boolean = environment.enableSSL;
    const protocol = "http" + (https ? "s" : "") + "://";
    // Deployed environments (enableSSL) reach the backend through the host
    // front-door nginx on 443, which routes to each service by URL path —
    // no port in the URL. Local dev connects straight to the service port.
    if (https) {
      return protocol + this.apiHostname + "/" + domain;
    }
    return protocol + this.apiHostname + ":" + this.getServicePort(serviceName) + "/" + domain;
  }

  /**
   * Generates HTTP headers required for API requests.
   *
   * This function creates an object containing HTTP headers such as content type, authorization token,
   * user identifier, school account ID, request origin, and timezone.
   * Values are retrieved from the persistency service and utility functions.
   *
   * @returns {any} An object containing the HTTP headers for API requests.
   */
  getHeaders(): any {
    const accountId: string = this.persistency.getUserAccountId();
    const schoolId: string = this.persistency.getSchoolAccountId();
    const identifier: string = this.persistency.getIdentifier();

    return {
      'Content-Type': 'application/json',
      'Identifier': identifier ? identifier : '',
      'AccountId': accountId ? accountId : '',
      'SchoolAccountId': schoolId ? schoolId : '',
      'RequestOrigin': RequestOrigin.BROWSER,
      'AppUserType': AppUserType.SCHOOL,
      'TimeZone': getTimeZone()
    };
  }

}